import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, exhaustMap, map, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { inviteFailure, loginFailure } from './account.actions';

import { JwtService } from 'src/app/core/services/jwt.service';
import { CredentialsLogin } from 'src/app/core/interfaces';

import {
  AccountsService,
  AuthResponse,
  GetAccountResponse,
} from '../../proto/consulate';
import { AccountState } from './account.reducer';
import { AccountActions } from './action-types';
import { DefaultConst } from '../../constants/default.constant';
import { CookieService } from '../../services/cookie.service';

@Injectable({
  providedIn: 'root',
})
export class AccountEffects {
  constructor(
    private accountsService: AccountsService,
    private actions$: Actions,
    private jwtService: JwtService,
    private router: Router,
    private cookieService: CookieService
  ) {}

  setAuth = (res: AuthResponse) => {
    const adminAccountState: AccountState =
      this.accountsService.prepAccountState(res.account, res.accessToken);

    this.router.navigateByUrl('/');
    return AccountActions.loginSuccess(adminAccountState);
  };

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.login),
      exhaustMap((action) => {
        const credentials: CredentialsLogin = {
          email: action.email,
          password: action.password,
        };

        return this.accountsService.attemptAuth(credentials).pipe(
          map(this.setAuth),
          catchError((error) => of(loginFailure(error)))
        );
      })
    )
  );

  acceptInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.acceptInvitation),
      exhaustMap((action) => {
        return this.accountsService.acceptInvitation(action.params).pipe(
          map(this.setAuth),
          catchError((error) => of(inviteFailure(error)))
        );
      })
    )
  );

  getAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.getAccount),
      concatMap(() => {
        this.cookieService.destroyCookie(DefaultConst.SISENSE_JWT_COOKIE_NAME);
        if (this.jwtService.getToken()) {
          return this.accountsService.getAccount().pipe(
            map((res: GetAccountResponse) => {
              const adminAccountState: AccountState =
                this.accountsService.prepAccountState(res);
              return AccountActions.getAccountSuccess(adminAccountState);
            }),
            catchError(() => {
              this.accountsService.purgeAuth();
              return of(AccountActions.purged());
            })
          );
        }

        this.accountsService.purgeAuth();
        return of(AccountActions.purged());
      })
    )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.logout),
        tap(() => {
          this.accountsService.purgeAuth();
          this.router.navigateByUrl('/login');
        }),
        concatMap(() => of(AccountActions.logoutSuccessful()))
      )
    // { dispatch: false }
  );
}
